import React, { useMemo, useState, useEffect } from 'react';
import { CustomIcon } from '../../UI';
import './UniversalFilterModal.scss';

const UniversalFilterModal = ({ 
  open,
  isOpen, 
  onClose, 
  onApply,
  onReset,
  onChange,
  sections,
  values,
  filterConfig,
  initialFilters = {},
  title = "Add Filters"
}) => {
  const resolvedOpen = open !== undefined ? open : isOpen;
  const resolvedSections = sections || filterConfig?.sections || [];
  const isControlled = values && typeof onChange === 'function';

  const [internalFilters, setInternalFilters] = useState({});
  const [searchQueries, setSearchQueries] = useState({});
  const [expandedPills, setExpandedPills] = useState({});

  const filters = isControlled ? values : internalFilters;

  const buildEmptyFilters = () => {
    const empty = {};
    (resolvedSections || []).forEach(section => {
      (section.fields || []).forEach(field => {
        const defaultValue = field.defaultValue;
        if (defaultValue !== undefined) {
          empty[field.key] = defaultValue;
          return;
        }

        if (field.type === 'multiSelectPills' || field.type === 'multiStatusPills' || field.type === 'checkboxGroup') {
          empty[field.key] = [];
          return;
        }

        if (field.type === 'segmentedSingle') {
          empty[field.key] = '';
          return;
        }

        empty[field.key] = '';
      });
    });
    return empty;
  };

  useEffect(() => {
    if (!resolvedOpen) return;

    if (!isControlled) {
      const base = buildEmptyFilters();
      const next = { ...base, ...initialFilters };
      setInternalFilters(next);
    }
  }, [resolvedOpen, resolvedSections, initialFilters, isControlled, filterConfig]);

  const setFieldValue = (key, value) => {
    if (isControlled) {
      onChange(key, value);
      return;
    }

    setInternalFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleMultiValue = (key, value) => {
    const current = filters?.[key] || [];
    const updated = current.includes(value)
      ? current.filter(item => item !== value)
      : [...current, value];
    setFieldValue(key, updated);
  };

  const handleApply = () => {
    onApply?.(filters);
    onClose?.();
  };

  const handleReset = () => {
    const reset = buildEmptyFilters();
    if (isControlled) {
      Object.keys(reset).forEach(key => onChange(key, reset[key]));
    } else {
      setInternalFilters(reset);
    }
    onReset?.(reset);
    if (!onReset) {
      onApply?.(reset);
    }
  };

  const updateSearch = (key, value) => {
    setSearchQueries(prev => ({ ...prev, [key]: value }));
  };

  const togglePillsExpanded = (key) => {
    setExpandedPills(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getSelectedLabels = (field) => {
    const current = filters?.[field.key] || [];
    return field.options
      .filter(option => current.includes(option.value))
      .map(option => option.label);
  };

  const renderPillSummary = (field) => {
    const selectedLabels = getSelectedLabels(field);
    if (selectedLabels.length === 0) {
      return <span className="pill-placeholder">{field.placeholder || 'Select'}</span>;
    }

    const visible = selectedLabels.slice(0, 3);
    const hiddenCount = selectedLabels.length - visible.length;

    return (
      <>
        {visible.map(label => (
          <span key={label} className="pill-chip">{label}</span>
        ))}
        {hiddenCount > 0 && (
          <>
            <span className="pill-ellipsis">...</span>
            <span className="pill-count">{hiddenCount}</span>
          </>
        )}
      </>
    );
  };

  const renderSelectField = (field, fieldValue) => {
    if (field.searchable) {
      const searchValue = searchQueries[field.key] ?? '';
      return (
        <div className="filter-field">
          <label>{field.label}</label>
          <div className="select-search">
            <input
              type="text"
              placeholder={field.placeholder || 'Text'}
              value={searchValue}
              onChange={(e) => updateSearch(field.key, e.target.value)}
              onBlur={() => {
                const match = field.options.find(option => option.label === searchValue);
                if (match) {
                  setFieldValue(field.key, match.value);
                }
              }}
            />
            <div className="select-search__icon">
              <CustomIcon name="down" size={16} type="svg" />
            </div>
            {searchValue && (
              <div className="select-search__menu">
                {field.options
                  .filter(option => option.label.toLowerCase().includes(searchValue.toLowerCase()))
                  .slice(0, 6)
                  .map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className="select-search__option"
                      onClick={() => {
                        setFieldValue(field.key, option.value);
                        updateSearch(field.key, option.label);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="filter-field">
        <label>{field.label}</label>
        <div className="dropdown-input">
          <select
            value={fieldValue}
            onChange={(e) => setFieldValue(field.key, e.target.value)}
          >
            <option value="">{field.placeholder || `Select ${field.label}`}</option>
            {field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <CustomIcon name="down" size={16} type="svg" />
        </div>
      </div>
    );
  };

  const renderCheckboxGroup = (field) => {
    const fieldValue = filters?.[field.key] || [];
    if (field.groups && field.groups.length > 0) {
      return (
        <div className="filter-field">
          <label>{field.label}</label>
          <div className="checkbox-columns">
            {field.groups.map(group => (
              <div key={group.title} className="checkbox-column">
                <div className="checkbox-column__title">{group.title}</div>
                <div className="checkbox-group">
                  {group.options.map(option => (
                    <label key={option.value} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={fieldValue.includes(option.value)}
                        onChange={() => toggleMultiValue(field.key, option.value)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="filter-field">
        <label>{field.label}</label>
        <div className="checkbox-group">
          {field.options.map(option => (
            <label key={option.value} className="checkbox-label">
              <input
                type="checkbox"
                checked={fieldValue.includes(option.value)}
                onChange={() => toggleMultiValue(field.key, option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderField = (field) => {
    const fieldValue = filters?.[field.key] ?? '';

    switch (field.type) {
      case 'text':
        return (
          <div className="filter-field">
            <label>{field.label}</label>
            <input
              type="text"
              placeholder={field.placeholder || 'Text'}
              value={fieldValue}
              onChange={(e) => setFieldValue(field.key, e.target.value)}
            />
          </div>
        );

      case 'date':
        return (
          <div className="filter-field">
            <label>{field.label}</label>
            <div className="input-with-icon">
              <input
                type="date"
                placeholder={field.placeholder || 'Date'}
                value={fieldValue}
                onChange={(e) => setFieldValue(field.key, e.target.value)}
              />
              {/* <span className="icon">
                <CustomIcon name="calendar-02" size={16} />
              </span> */}
            </div>
          </div>
        );

      case 'select':
        return renderSelectField(field, fieldValue);

      case 'segmentedSingle':
        return (
          <div className="filter-field">
            <label>{field.label}</label>
            <div className="pill-group">
              {field.options.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`pill ${fieldValue === option.value ? 'is-active' : ''}`}
                  onClick={() => setFieldValue(field.key, fieldValue === option.value ? '' : option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 'multiStatusPills':
        return (
          <div className="filter-field">
            <label>{field.label}</label>
            <div className="pill-group">
              {field.options.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`pill ${(filters?.[field.key] || []).includes(option.value) ? 'is-active' : ''}`}
                  onClick={() => toggleMultiValue(field.key, option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 'multiSelectPills':
        return (
          <div className="filter-field">
            <label>{field.label}</label>
            <button type="button" className="pill-input" onClick={() => togglePillsExpanded(field.key)}>
              <div className="pill-input__content">
                {renderPillSummary(field)}
              </div>
              <CustomIcon name="down" size={16} type="svg" />
            </button>
            {expandedPills[field.key] && (
              <div className="pill-options">
                {field.options.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={`pill ${(filters?.[field.key] || []).includes(option.value) ? 'is-active' : ''}`}
                    onClick={() => toggleMultiValue(field.key, option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );

      case 'checkboxGroup':
        return renderCheckboxGroup(field);

      default:
        return null;
    }
  };

  if (!resolvedOpen || !resolvedSections.length) return null;

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button type="button" className="close-btn" onClick={onClose}>
            <CustomIcon name="close" size={20} />
          </button>
        </div>

        <div className="modal-content">
          {resolvedSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="filter-section">
              {section.title && <h3 className="section-title">{section.title}</h3>}
              {(section.rows && section.rows.length > 0
                ? section.rows
                : [{ fields: section.fields, columns: section.columns }]
              ).map((row, rowIndex) => (
                <div
                  key={`${sectionIndex}-${rowIndex}`}
                  className={`filter-row ${row.columns ? `filter-row--${row.columns}` : ''}`}
                >
                  {row.fields.map((field, fieldIndex) => (
                    <div key={fieldIndex} className={field.fullWidth ? 'filter-span-full' : ''}>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <button type="button" className="reset-btn" onClick={handleReset}>
            Reset
          </button>
          <button type="button" className="apply-btn" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default UniversalFilterModal;
